import React, { useEffect, useState } from "react";
import { cqAPI, Auth } from "../../lib/Auth";
import {
  Box,
  Flex,
  SimpleGrid,
  Text,
  Button,
  VStack,
  useToast,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Input,
  Checkbox,
  Radio,
  RadioGroup,
  Stack,
  useColorModeValue
} from "@chakra-ui/react";
import dialogManager from "../DialogManager/DialogManager";
import PaymentMethods from "./PaymentMethods";
import EditableText from "../EditableText/EditableText";
import moment from 'moment';
import {
  usageTypeOf,
  isLicensedPrice,
  isTieredPrice,
  isZeroPrice,
  unitAmountOf,
  describeLicensedTiers,
  isProviderLicenseProduct,
  describeProviderLicenses,
  defaultProviderLicenseAmount,
  isPerLocationAddonPrice,
  perLocationPriceLabel,
} from "./priceFormat";

function Subscription() {
  const [editMode, setEditMode] = useState(false);
  const [stagedChange, setStagedChange] = useState(null);
  const [availableBundles, setAvailableBundles] = useState([]);
  const [activeBundle, setActiveBundle] = useState(null);
  const [displayedBundle, setDisplayedBundle] = useState(null);
  const [card, setCard] = useState({});
  const [stagedChanges, setStagedChanges] = useState([]);
  const [availableProducts, setAvailableProducts] = useState([]);
  const [scheduledBundle, setScheduledBundle] = useState({});
  const [currentUsages, setCurrentUsages] = useState([]);
  const [existingCurrentUsages, setExistingCurrentUsages] = useState([]);
  const [activeAddons, setActiveAddons] = useState([]);
  const [availableAddons, setAvailableAddons] = useState([]);
  const headerBackgroundColor = useColorModeValue("#F1F1F1", "#303343");
  const textColor = useColorModeValue("black", "white");
  const [isPartner, setIsPartner] = useState(false);
  const [trialDays, setTrialDays] = useState("");
  const [sendInvoice, setSendInvoice] = useState(false);
  const [subscription, setSubscription] = useState({});

  const [endTrialLoading, setEndTrialLoading] = useState(false);

  const [isPromotionModalOpen, setIsPromotionModalOpen] = useState(false);
  // MULTI PROMO: array of codes for the modal
  const [promotionCodes, setPromotionCodes] = useState([""]);
  const [promotionCallback, setPromotionCallback] = useState(null);
  // SUPERADMIN ONLY: internal whole-dollar discounts. `internalCouponsSupported`
  // stays false until the API answers the listing, so on an API that does not have
  // the endpoint yet the whole block simply never renders.
  const [internalCoupons, setInternalCoupons] = useState([]);
  const [internalCouponsSupported, setInternalCouponsSupported] = useState(false);
  const [showCouponForm, setShowCouponForm] = useState(false);
  const [couponAmount, setCouponAmount] = useState("");
  const [couponDuration, setCouponDuration] = useState("once");
  const [couponMonths, setCouponMonths] = useState("");
  const [isCreatingCoupon, setIsCreatingCoupon] = useState(false);
  const [ignoreLimit, setIgnoreLimit] = useState([])
  
  const isLoadedPartner = window.location.hostname.includes("adec") || window.location.hostname.includes("a-dec");

  const [subscriptionStartDate, setSubscriptionStartDate] = useState("");
  const [autoScaleMetered, setAutoScaleMetered] = useState(true);
  const [hasSiblingLocations, setHasSiblingLocations] = useState(false);
  const [requiresPaymentMethod, setRequiresPaymentMethod] = useState(false);
  // SS-4412: both payment methods (a bank account as primary, the card as backup)
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [bankAccountPending, setBankAccountPending] = useState(null);
  const [bankAccountAvailable, setBankAccountAvailable] = useState(false);
  // SS-4462: the group's provider license usage (GET /settings/subscriptions), and the
  // number of licenses a staged bundle containing the providers product subscribes with
  const [providerLicenses, setProviderLicenses] = useState(null);
  const [providerLicenseAmount, setProviderLicenseAmount] = useState("1");


  const toast = useToast();

  const fetchAddons = async () => {
    const response = await cqAPI.get("/settings/subscriptions/addons");
    setAvailableAddons(response.data?.addons || []);
    setActiveAddons(response.data?.active_addons || []);
  };

  const fetchBundles = async () => {
    const response = await cqAPI.get("/settings/subscriptions/bundles");
    setAvailableBundles(response.data?.bundles || []);
    setActiveBundle(response.data?.active_bundle || null);
    setScheduledBundle(response.data?.scheduled);
    setDisplayedBundle(response.data?.active_bundle || null);
    setIgnoreLimit(response.data?.ignoreLimit || [])
  };

  const applyPaymentMethods = (response) => {
    setCard(response.data?.paymentMethod || {});
    setPaymentMethods(response.data?.paymentMethods || []);
    setBankAccountPending(response.data?.bank_account_pending || null);
    setBankAccountAvailable(response.data?.bank_account_available === true);
  };

  // after a payment method changes: the write's own answer at once, then the payment methods
  // read again (only those, so staged plan changes survive)
  const refreshPaymentMethods = async (changed) => {
    if (changed?.paymentMethods) {
      setPaymentMethods(changed.paymentMethods);
      setBankAccountPending(changed.bank_account_pending || null);
    }
    const response = await cqAPI.get("/settings/subscriptions");
    if (response?.type === "ok") {
      applyPaymentMethods(response);
    }
  };

  const fetchPlans = async () => {
    const response = await cqAPI.get("/settings/subscriptions");
    setAvailableProducts(response.data?.availableProducts);
    setCurrentUsages(response.data?.currentUsages);
    setExistingCurrentUsages(response.data?.currentUsages);
    applyPaymentMethods(response);
    setSubscription(response.data?.subscription);
    setHasSiblingLocations(response.data?.has_sibling_locations || false);
    setProviderLicenses(response.data?.provider_licenses || null);
    // Normalize promotions to array for the modal
    const promos = response.data?.promotion;
    setPromotionCodes(
      Array.isArray(promos)
        ? (promos.length ? promos : [""])
        : (promos ? [promos] : [""])
    );
  };

  useEffect(() => {
    fetchPlans().then(() => {
      fetchBundles();
      fetchAddons();
    });
    cqAPI.get('/isloggedin').then(data=>{
      setIsPartner(data?.data?.user?.business?.partner?.enable_start_date ? true : false);
    });
  }, []);

  const toggleEditMode = () => {
    setEditMode(!editMode);
    if (!editMode) {
      setStagedChange(null);
      setDisplayedBundle(activeBundle);
    }
  };

  const stageChange = (bundle_id) => {
    setStagedChange(bundle_id);
    setDisplayedBundle(bundle_id);
  };

  const [isSubscriptionUpdate, setIsSubscriptionUpdate] = useState(false);

  // Internal discounts are superadmin-only on the server too, so nobody else even asks.
  // A response that is not {type:'ok'} — including an API that predates the endpoint —
  // leaves the block hidden rather than showing a control that cannot work.
  const fetchInternalCoupons = async () => {
    try {
      const response = await cqAPI.get("/settings/subscriptions/coupons");
      if (response?.type === "ok") {
        setInternalCoupons(response.data?.coupons || []);
        setInternalCouponsSupported(true);
        return;
      }
    } catch (error) {
      console.error("Cannot load internal discounts:", error);
    }
    setInternalCoupons([]);
    setInternalCouponsSupported(false);
  };

  const resetCouponForm = () => {
    setShowCouponForm(false);
    setCouponAmount("");
    setCouponDuration("once");
    setCouponMonths("");
  };

  // Stripe holds cents; discounts created here are always whole dollars, but a legacy
  // fixed-dollar coupon can carry cents and has to show its exact value.
  const formatCouponAmount = (coupon) => {
    const amount = (coupon.amount_off || 0) / 100;
    const formatted = Number.isInteger(amount) ? `$${amount}` : `$${amount.toFixed(2)}`;
    return coupon.currency && coupon.currency !== "usd"
      ? `${formatted} ${coupon.currency.toUpperCase()}`
      : formatted;
  };

  const describeCouponDuration = (coupon) => {
    if (coupon.duration === "once") {
      return "one time";
    }
    if (coupon.duration === "forever") {
      return "forever";
    }
    return `for ${coupon.duration_in_months} month${coupon.duration_in_months === 1 ? "" : "s"}`;
  };

  const describeCouponRestriction = (coupon) => {
    const notes = [];
    if (coupon.restricted_products) {
      notes.push("limited to certain products");
    }
    if (coupon.max_redemptions) {
      notes.push(`max ${coupon.max_redemptions} redemptions`);
    }
    if (coupon.redeem_by) {
      notes.push(`redeemable until ${moment.unix(coupon.redeem_by).format("MMM D, YYYY")}`);
    }
    return notes.join(", ");
  };

  // Adds one more entry to the array the modal already sends. Codes already typed keep
  // their place: an empty box is filled first, otherwise the entry is appended.
  const addPromotionEntry = (code) => {
    setPromotionCodes((prev) => {
      if (prev.some((entry) => (entry || "").trim() === code)) {
        return prev;
      }
      const emptyIndex = prev.findIndex((entry) => !(entry || "").trim());
      if (emptyIndex === -1) {
        return [...prev, code];
      }
      const next = [...prev];
      next[emptyIndex] = code;
      return next;
    });
  };

  const handleCreateCoupon = async () => {
    const amount = String(couponAmount).trim();
    if (!/^\d+$/.test(amount) || Number(amount) < 1) {
      toast({
        title: "Validation Error",
        description: "Enter the discount as a whole number of dollars, at least $1.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
      return;
    }
    const months = String(couponMonths).trim();
    if (couponDuration === "repeating" && (!/^\d+$/.test(months) || Number(months) < 1)) {
      toast({
        title: "Validation Error",
        description: "Enter the number of months as a whole number, at least 1.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    try {
      setIsCreatingCoupon(true);
      const response = await cqAPI.post("/settings/subscriptions/coupon", {
        amount: Number(amount),
        duration: couponDuration,
        ...(couponDuration === "repeating" ? { duration_in_months: Number(months) } : {}),
      });
      if (response.type === "error") {
        toast({
          title: "Error",
          description: response.msg,
          status: "error",
          duration: 5000,
          isClosable: true,
        });
        setIsCreatingCoupon(false);
        return;
      }
      addPromotionEntry(response.data.code);
      toast({
        title: response.data.reused ? "Existing discount added" : "Discount created",
        description: response.data.reused
          ? `Using the existing discount "${response.data.matched_name}".`
          : `${response.data.name} was created and added.`,
        status: "success",
        duration: 5000,
        isClosable: true,
      });
      resetCouponForm();
      await fetchInternalCoupons();
      setIsCreatingCoupon(false);
    } catch (error) {
      toast({
        title: "Error",
        description: "An error occurred while creating the discount.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
      setIsCreatingCoupon(false);
    }
  };

  const openPromotionModal = (callback, subscriptionUpdate = false) => {
    setPromotionCallback(() => callback);
    setIsSubscriptionUpdate(subscriptionUpdate);
    setAutoScaleMetered(true); // reset to default each time modal opens
    resetCouponForm();
    if (Auth.user?.isSuperAdmin) {
      fetchInternalCoupons();
    }
    setIsPromotionModalOpen(true);
  };

  // The providers plan a staged (not yet active) bundle is about to subscribe to, if any.
  const stagedProviderLicensePlan = () => {
    if (!stagedChange || stagedChange === activeBundle) {
      return null;
    }
    const bundle = availableBundles.find((b) => b._id === stagedChange);
    const item = bundle?.items?.find(
      ([product, plan]) => isProviderLicenseProduct(product) && !isZeroPrice(plan)
    );
    return item && currentUsages.find((usage) => usage.plan === item[1].id && usage.isNew)
      ? item[1]
      : null;
  };

  const handleSaveChanges = async () => {
    if (activeBundle == null && stagedChange != null) {
      openPromotionModal((enteredPromotionCodes, selectedStartDate, doInvoice, days, autoScale) => {
        if (enteredPromotionCodes === null && selectedStartDate === null) {
          return;
        }
        saveChanges(enteredPromotionCodes, selectedStartDate, doInvoice, days, autoScale);
      }, true);
    } else {
      // Always send promotions as array (empty when none)
      saveChanges([], null);
    }
  };

  // Always include `promotion` as array (empty if none)
  const saveChanges = async (promotionCodesArg = [], startDate, doInvoice, days, autoScale = true) => {
    if (stagedProviderLicensePlan() && !/^[1-9]\d*$/.test(providerLicenseAmount)) {
      dialogManager.open({
        onDeny: () => {},
        acceptText: "OK",
        body: "Enter the number of provider licenses: a whole number of at least 1.",
        header: "Error",
      });
      return;
    }
    const simplifiedChanges = simplifyStagedChanges();

    const codes = Array.isArray(promotionCodesArg)
      ? promotionCodesArg.map((c) => (c || "").trim()).filter(Boolean)
      : [];

    const response = await cqAPI.put(
      `/settings/subscriptions/batch-update?bundle_id=${stagedChange || ""}`,
      {
        changes: simplifiedChanges,
        promotion: codes, // array, possibly empty
        ...(startDate
          ? { start_date: startDate }
          : {}
        ),
        ...(doInvoice
          ? { invoiced: true }
          : {}
        ),
        ...(days
          ? { trial_period_days: days }
          : {}
        ),
        ...(autoScale === false
          ? { auto_scale_metered: false }
          : {}
        )
      }
    );

    if (response.type === "error") {
      dialogManager.open({
        onDeny: () => {},
        acceptText: "OK",
        body: response.msg,
        header: "Error",
      });
      return;
    } else {
      fetchPlans().then(() => {
        fetchBundles();
        fetchAddons();
      });
      setStagedChange(null);
      setStagedChanges([]);
      setEditMode(false);
      setActiveBundle(stagedChange);
      setStagedChange(null);
    }
  };

  const requestCreditCardChange = async () => {
    const response = await cqAPI.requestCreditCard({}).catch((error) => {
      console.error(error);
    });
    if (response) {
      // the dialog answers with Stripe's PaymentMethod: its last4 and brand are under .card
      const updated = { last4: response.card?.last4, brand: response.card?.brand };
      setCard(updated);
      setPaymentMethods((methods) => {
        const bankIsPrimary = methods.some((pm) => pm.type === "us_bank_account" && pm.default);
        const others = methods.filter((pm) => pm.type !== "card");
        return [...others, { type: "card", ...updated, default: !bankIsPrimary }];
      });
      await refreshPaymentMethods();
    }
  };

  // Accept string or array; always POST array when applying directly
  const applyPromotionCode = async (promotionInput) => {
    const arr = Array.isArray(promotionInput)
      ? promotionInput.map((s) => String(s || "").trim()).filter(Boolean)
      : (promotionInput ? [String(promotionInput).trim()] : []);
    if (!arr.length) {
      return;
    }
    const response = await cqAPI.post("/settings/subscription/promotion", {
      promotion: arr,
    });
    if (response.type === "error") {
      dialogManager.open({
        onDeny: () => {},
        acceptText: "OK",
        body: response.msg,
        header: "Error",
      });
      return;
    } else {
      toast({
        title: "Promotion Applied",
        description: "Your promotion has been successfully applied.",
        status: "success",
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const stageBatchChange = (change) => {
    setStagedChanges((prev) => [...prev, change]);
    const product = availableProducts.find((p) => p.id === change.product_id);

    setCurrentUsages((prevCurrentUsages) => {
      const isAlreadySubscribedToAnotherPlan = prevCurrentUsages.find((u) =>
        product.plans.find((p) => p.id === u.plan)
      );

      let updatedUsages;
      let newPlan;
      switch (change.type) {
        case "subscribe":
          newPlan = product.plans.find((p) => p.id === change.plan_id);
          if (isAlreadySubscribedToAnotherPlan) {
            updatedUsages = [
              ...prevCurrentUsages.filter(
                (x) => x._id !== isAlreadySubscribedToAnotherPlan._id
              ),
              {
                ...isAlreadySubscribedToAnotherPlan,
                sub_id: undefined,
                sub_item_id: undefined,
                plan: change.plan_id,
                usage_type: newPlan.usage_type,
                // SS-4462: a staged license count wins over the plan being replaced
                amount: change.amount ?? isAlreadySubscribedToAnotherPlan.amount,
                _id: `temp-${Math.random().toString()}`,
                isNew: true,
                limit: newPlan.limit
              },
            ];
          } else {
            updatedUsages = [
              ...prevCurrentUsages,
              {
                plan: change.plan_id,
                usage_type: newPlan?.usage_type,
                _id: `temp-${Math.random().toString()}`,
                amount: change.amount ?? 1,
                isNew: true,
                limit: newPlan?.limit
              },
            ];
          }
          break;
        case "changeLicenses":
          updatedUsages = [
            ...prevCurrentUsages.filter(
              (x) => x._id !== isAlreadySubscribedToAnotherPlan._id
            ),
            {
              ...isAlreadySubscribedToAnotherPlan,
              amount: change.amount,
            },
          ];
          break;
        case "delete":
          updatedUsages = prevCurrentUsages.filter(
            (u) => u._id !== change.usage_id
          );
          break;
        default:
          updatedUsages = prevCurrentUsages;
          break;
      }

      return updatedUsages;
    });
  };

  useEffect(() => {
    setCurrentUsages(existingCurrentUsages);

    if (activeBundle === displayedBundle) {
      return;
    }
    if (!displayedBundle) {
      existingCurrentUsages.forEach((usage) => {
        const product = availableProducts.find((p) =>
          p.plans.find((plan) => plan.id === usage.plan)
        );
        stageBatchChange({
          type: "delete",
          product_id: product.id,
          plan_id: usage.plan,
          usage_id: usage._id,
        });
      });
      return;
    }

    const selectedBundlePlan = availableBundles.find(
      (b) => b._id === displayedBundle
    );
    // SS-4462: a bundle that sells provider licenses subscribes with the licenses the group
    // still needs (its activated providers not covered by the other locations), at least
    // one; the admin can change it below.
    // A location that already holds the providers product keeps its own count.
    const providerItem = selectedBundlePlan.items.find(
      ([product, plan]) => isProviderLicenseProduct(product) && !isZeroPrice(plan)
    );
    const heldProviderUsage = providerItem && existingCurrentUsages.find((usage) =>
      availableProducts
        .find((p) => p.id === providerItem[0].id)
        ?.plans.find((plan) => plan.id === usage.plan)
    );
    const initialProviderLicenses = String(
      heldProviderUsage?.amount || defaultProviderLicenseAmount(providerLicenses)
    );
    setProviderLicenseAmount(initialProviderLicenses);
    selectedBundlePlan.items.forEach((item) => {
      stageBatchChange({
        type: "subscribe",
        product_id: item[0].id,
        plan_id: item[1].id,
        ...(item === providerItem ? { amount: Number(initialProviderLicenses) } : {}),
      });
    });

    existingCurrentUsages.forEach((usage) => {
      const product = availableProducts.find((p) =>
        p.plans.find((plan) => plan.id === usage.plan)
      );
      const toBeAdded = selectedBundlePlan.items.find(
        (item) => item[1].id === usage.plan
      );
      if (!toBeAdded) {
        stageBatchChange({
          type: "delete",
          product_id: product.id,
          plan_id: usage.plan,
          usage_id: usage._id,
        });
      }
    });
  }, [displayedBundle]);

  // SS-4462: the license count typed for a staged providers subscription. Only a staged
  // NEW usage of that plan changes; an existing subscription is edited by its own control.
  const stageProviderLicenseAmount = (planId, value) => {
    setProviderLicenseAmount(value);
    if (!/^[1-9]\d*$/.test(value)) {
      return;
    }
    setCurrentUsages((prev) =>
      prev.map((usage) =>
        usage.plan === planId && usage.isNew ? { ...usage, amount: Number(value) } : usage
      )
    );
  };

  function simplifyStagedChanges() {
    const changes = currentUsages.reduce((acc, usage) => {
      const existing = existingCurrentUsages.find((p) => p.plan === usage.plan);
      if (!existing) {
        acc.push({ ...usage, type: "subscribe" });
      } else if (existing.amount !== usage.amount) {
        acc.push({ ...usage, type: "changeLicenses" });
      }
      return acc;
    }, []);

    const deleted = existingCurrentUsages.reduce((acc, usage) => {
      const stillExists = currentUsages.find((p) => p.plan === usage.plan);
      if (!stillExists) {
        acc.push({ ...usage, type: "delete" });
      }
      return acc;
    }, []);

    return [...deleted, ...changes];
  }

  const formatTieredPricing = (
    tiers,
    tiers_mode,
    unit,
    usage_type,
    fallbackPrice = 0
  ) => {
    if (isLoadedPartner && usage_type === "licensed") {
      return null;
    }
    if (!tiers || tiers.length === 0) {
      const price = (fallbackPrice / 100).toFixed(3);
      const perUnitLabel = unit ? `/${unit}` : "";
      const licenseSuffix = usage_type === "licensed" ? " per month" : "";
      return (
        <Text>
          Price: ${price}
          {perUnitLabel}
          {licenseSuffix}
        </Text>
      );
    }

    return tiers.map((tier, index) => {
      let costParts = [];
      const hasFlat = tier.flat_amount_decimal > 0;
      const hasUnit = tier.unit_amount_decimal > 0;

      if (hasFlat) {
        costParts.push(`$${(tier.flat_amount_decimal / 100).toFixed(2)}`);
      }

      if (hasUnit) {
        const perUnitLabel = unit ? `/${unit}` : "";
        const licenseSuffix = usage_type === "licensed" ? " per month" : "";
        costParts.push(
          `$${(tier.unit_amount_decimal / 100).toFixed(3)}${perUnitLabel}${licenseSuffix}`
        );
      }

      if (!hasFlat && !hasUnit) {
        costParts.push("$0.00");
      }

      const priceString = isLoadedPartner && (index === 0) ? 'included' : costParts.join(" + ");
      // a tier starts one past the previous tier's up_to, so adjacent tiers never share a count
      const start = index > 0 ? (tiers[index - 1]?.up_to || 0) + 1 : 0;

      if (tier.up_to === null) {
        return (
          <Text key={index}>
            Anything more or equal than {start}{" "}
            {unit || "units"}: {priceString}
          </Text>
        );
      } else {
        return (
          <Text key={index}>
            Tier {index + 1} between {start} and{" "}
            {tier.up_to} {unit || "units"}: {priceString}
          </Text>
        );
      }
    });
  };

  const changeLicenses = async (product_id, plan_id, usage_id, amount) => {
    const response = await cqAPI.put("/settings/subscriptions", {
      product_id,
      plan_id,
      usage_id,
      amount,
    });

    if (response.type === "error") {
      dialogManager.open({
        onDeny: () => {},
        acceptText: "OK",
        body: response.msg,
        header: "Error",
      });
      return false;
    }

    await fetchPlans().then(() => fetchBundles());
    return true;
  };

  const getUsageForLicense = (product_id) => {
    const product = availableProducts.find((p) => p.id === product_id);
    const existing = existingCurrentUsages.find((usage) =>
      product.plans.find((pln) => pln.id === usage.plan)
    );
    return {
      amount: existing ? existing.amount : 1,
      existing_id: existing?._id,
    };
  };

  const subscribeAddon = async (addon_id) => {
    const response = await cqAPI.post(`/settings/subscriptions/${addon_id}`);
    if (response.type === "error") {
      dialogManager.open({
        onDeny: () => {},
        acceptText: "OK",
        body: response.msg,
        header: "Error",
      });
      return;
    }
    await fetchAddons();
  };

  const removeAddon = async (addon_id) => {
    const response = await cqAPI.del(`/settings/subscriptions/${addon_id}`);
    if (response.type === "error") {
      dialogManager.open({
        onDeny: () => {},
        acceptText: "OK",
        body: response.msg,
        header: "Error",
      });
      return;
    }
    await fetchAddons();
  };

  const UnscheduleActivation = async () => {
    await cqAPI.del('/settings/subscriptions/bundle-activation');
    fetchBundles();
  };

  const EndTrial = async () => {
    try {
      setEndTrialLoading(true);
      const response = await cqAPI.post('/settings/subscriptions/end-trial');
      if (response?.type === 'ok'){
        await fetchPlans();
      } else if (response?.type === 'error'){
        toast({
          title: "Error",
          description: response.msg,
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      }
    } finally {
      setEndTrialLoading(false);
    }
  };

  const isValidFutureDate = (maybeDateString) => {
    if (!maybeDateString) {
      return true;
    }
    const selected = moment(maybeDateString, 'YYYY-MM-DD').toDate();
    const now = new Date();
    return selected >= now;
  };

  const handleModalCloseOrCancel = () => {
    setTrialDays("");
    setSendInvoice(false);
    setIsPromotionModalOpen(false);
    setPromotionCodes([""]);
    setSubscriptionStartDate("");
    if (promotionCallback) {
      promotionCallback(null, null);
      setPromotionCallback(null);
    }
  };

  const handleModalContinue = () => {
    if (!isValidFutureDate(subscriptionStartDate)) {
      toast({
        title: "Invalid Date",
        description: "Please select a date strictly in the future, or leave it empty.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    setIsPromotionModalOpen(false);
    // Trim & filter empties => [] when none
    const codes = promotionCodes.map(c => (c || "").trim()).filter(Boolean);
    const date = subscriptionStartDate;
    const doInvoice = sendInvoice;
    const days = trialDays;
    setPromotionCodes([""]);
    setSubscriptionStartDate("");
    if (promotionCallback) {
      promotionCallback(codes, date, doInvoice, days, autoScaleMetered);
      setPromotionCallback(null);
    }
  };

  const removeLimit = async (plan)=>{
    const response = await cqAPI.del('/settings/subscriptions/remove-limit',{
      plan_id : plan.id
    })
    if (response.type === 'ok'){
      setIgnoreLimit([...ignoreLimit, plan.id])
    }
  }

  // NEW: remove one promo row (keep at least one input visible)
  const removePromotionAt = (idx) => {
    setPromotionCodes((prev) => {
      if (prev.length === 1) return [""];
      const next = prev.slice(0, idx).concat(prev.slice(idx + 1));
      return next.length ? next : [""];
    });
  };

  // SS-4462: a licensed price in words — graduated/volume tiers from the tiers themselves,
  // "Included" for a $0 price, otherwise the per-unit price — always "per month".
  const renderLicensedPrice = (product, plan, { addon = false } = {}) => {
    // an add-on item is billed once per subscribed location
    if (addon && isPerLocationAddonPrice(plan)) {
      return (
        <Text className="licensed-price">
          Price: {perLocationPriceLabel(plan)}
          {plan?.metadata?.limit && ` (limit: ${plan?.metadata?.limit})`}
        </Text>
      );
    }
    if (isTieredPrice(plan)) {
      return <Text className="licensed-price">Price: {describeLicensedTiers(plan, product.unit_label)}</Text>;
    }
    if (isZeroPrice(plan)) {
      return <Text className="licensed-price">Included</Text>;
    }
    return (
      <Text className="licensed-price">
        Price: ${(unitAmountOf(plan) / 100).toFixed(3)}/
        {product.unit_label ? product.unit_label : "unit"} per month
        {plan?.metadata?.limit && ` (limit: ${plan?.metadata?.limit})`}
      </Text>
    );
  };

  // "3 of 5 provider licenses in use" (group-wide) beside the providers license control;
  // nothing for other products or when the group is unlimited.
  const renderProviderLicenseSummary = (product) => {
    const summary = isProviderLicenseProduct(product) && describeProviderLicenses(providerLicenses);
    return summary ? (
      <Text className="provider-license-summary" fontSize="sm" color="gray.500">
        {summary}
      </Text>
    ) : null;
  };

  useEffect(() => {
    if (requiresPaymentMethod) {
      requestCreditCardChange()
        .catch(err => console.error('Cannot request credit card:', err))
        .finally(() => setRequiresPaymentMethod(false));
    }
  }, [requiresPaymentMethod]);

  return (
    <Box p={4} overflowY="auto" maxHeight="100vh" width="100%">
      <Flex justifyContent="right" padding="0px 0px 20px 0px">
        <PaymentMethods
          card={card}
          paymentMethods={paymentMethods}
          bankAccountPending={bankAccountPending}
          bankAccountAvailable={bankAccountAvailable}
          businessName={Auth.user?.business?.name}
          onUpdateCard={requestCreditCardChange}
          onChanged={refreshPaymentMethods}
        />
        {(Auth.user.isSuperAdmin || !isPartner) && !scheduledBundle?.bundle && (
          <Button
            marginLeft="10px"
            onClick={() =>
              openPromotionModal((enteredPromotionCodes, _selectedDate) => {
                if (enteredPromotionCodes && enteredPromotionCodes.length) {
                  applyPromotionCode(enteredPromotionCodes);
                }
              })
            }
          >
            Apply Promotion
          </Button>
        )}
      </Flex>

      <Tabs variant="enclosed">
        <TabList>
          <Tab>Subscription</Tab>
          {(Auth.user.isSuperAdmin || !isPartner) && (<Tab>Add-ons</Tab>)}
        </TabList>

        <TabPanels>
          <TabPanel>
            {(Auth.user.isSuperAdmin || Auth.user.isPartnerAdmin) && (<Flex justifyContent="right" padding="0px 0px 20px 0px">
              {!scheduledBundle?.bundle && (
                <Button onClick={toggleEditMode}>
                  {editMode ? "Cancel Changes" : "Change Plan"}
                </Button>
              )}
              {editMode && (
                <Button ml={4} colorScheme="teal" onClick={handleSaveChanges}>
                  Save Changes
                </Button>
              )}
              </Flex>)}

            <SimpleGrid columns={{ base: 1, md: 1, lg: 1 }} spacing="20px">
              {availableBundles
                .filter(
                  (bundle) =>
                    !scheduledBundle?.bundle ||
                    scheduledBundle?.bundle === bundle._id
                )
                .map((bundle, index) => (
                  <Box
                    key={index}
                    borderWidth="1px"
                    borderRadius="lg"
                    mb={4}
                    width="100%"
                  >
                    <Box
                      color={textColor}
                      p={4}
                      bg={headerBackgroundColor}
                      borderTopRadius="lg"
                      position="relative"
                    >
                      <Text>{bundle.name}</Text>
                      <Text>{bundle.description}</Text>
                      {displayedBundle === bundle._id && (
                        <Text color="green.500">
                          Selected {!bundle.active && "(grandfathered)"}
                        </Text>
                      )}

                      {displayedBundle === bundle._id &&
                        subscription.status === "trialing" &&
                        Auth.user &&
                        Auth.user.isSuperAdmin && (
                          <Flex align="center" justify="center">
                            <Text color="green.500" mr={2}>
                              Trial ends on{" "}
                              {moment(subscription.trial_end * 1000).format("L")}
                            </Text>
                            <Button
                              onClick={EndTrial}
                              isLoading={endTrialLoading}
                              isDisabled={endTrialLoading}
                              spinnerPlacement="start"
                            >
                              End Trial Now
                            </Button>
                          </Flex>
                        )}

                      {scheduledBundle?.bundle === bundle._id && (
                        <Flex align="center" justify="center">
                          <Text color="green.500" mr={2}>
                            Activate on{" "}
                            {moment(scheduledBundle.date + 3600000).format("L")}
                          </Text>
                          <Button onClick={() => UnscheduleActivation()}>
                            Unsubscribe
                          </Button>
                        </Flex>
                      )}
                    </Box>
                    <Box p={4}>
                      <VStack align="left">
                        {bundle.items.map(([product, plan], itemIndex) => (
                          <Flex justify="space-between" key={itemIndex} mb={4}>
                            <Box flex="1" textAlign="left" ml={4}>
                              <Flex align="center">
                              <Text fontWeight="bold" paddingRight="10px">
                                {product.name}: {!ignoreLimit.includes(plan.id) && plan.metadata && plan.metadata.limit && (`(${plan.metadata.limit} ${product.unit_label}s included)`)}
                              </Text>
                              {!ignoreLimit.includes(plan.id) && plan.metadata && plan.metadata.limit && (<Button id={`remove-limit-${product.name.replace(/\s+/g, '-')}`} onClick={()=>removeLimit(plan)}>Remove Limit</Button>)}</Flex>
                              {!isLicensedPrice(plan) ? (
                                <>
                                  {formatTieredPricing(
                                    plan.tiers,
                                    plan.tiers_mode,
                                    product.unit_label,
                                    usageTypeOf(plan),
                                    plan.unit_amount_decimal ||
                                      plan.amount ||
                                      0
                                  )}
                                </>
                              ) : (
                                <>
                                  {!isLoadedPartner && renderLicensedPrice(product, plan)}
                                  {/* SS-4462: a $0 licensed price is included, so it has no
                                      license count to edit */}
                                  {!isZeroPrice(plan) && (activeBundle === bundle._id ? (
                                    <Flex align="center" gap={4}>
                                      <EditableText
                                        initialValue={
                                          getUsageForLicense(product.id).amount
                                        }
                                        onSave={(e) =>
                                          changeLicenses(
                                            product.id,
                                            plan.id,
                                            getUsageForLicense(product.id)
                                              .existing_id,
                                            parseInt(e)
                                          )
                                        }
                                        placeholder="Licenses"
                                        pattern={/^[1-9]\d*$/}
                                        invalid="Only whole numbers of at least 1 are acceptable."
                                        // SS-4463: an api refusal is explained by changeLicenses'
                                        // Error dialog; the value itself was valid
                                        showValidationToast={false}
                                        editable={!isPartner || Auth.user.isSuperAdmin}
                                      />
                                      {renderProviderLicenseSummary(product)}
                                    </Flex>
                                  ) : (
                                    displayedBundle === bundle._id && (
                                      isProviderLicenseProduct(product) && stagedProviderLicensePlan()?.id === plan.id ? (
                                        <Flex align="center" gap={4}>
                                          <Text>Provider licenses:</Text>
                                          <Input
                                            className="provider-license-amount"
                                            type="number"
                                            min={1}
                                            step={1}
                                            width="100px"
                                            value={providerLicenseAmount}
                                            onChange={(e) => stageProviderLicenseAmount(plan.id, e.target.value)}
                                          />
                                          {renderProviderLicenseSummary(product)}
                                        </Flex>
                                      ) : (
                                        <Flex justify="flex-end" align="center">
                                          {getUsageForLicense(product.id).amount}
                                        </Flex>
                                      )
                                    )
                                  ))}
                                </>
                              )}
                            </Box>
                          </Flex>
                        ))}
                      </VStack>
                      {editMode && displayedBundle !== bundle._id && (
                        <Button
                          id={`subscribe-${bundle.name.replace(/\s+/g, '-').toLowerCase()}`}
                          size="sm"
                          mt={4}
                          onClick={() => stageChange(bundle._id)}
                        >
                          Subscribe
                        </Button>
                      )}
                      {editMode && displayedBundle === bundle._id && (
                        <Button
                          size="sm"
                          mt={4}
                          onClick={() => stageChange(null)}
                        >
                          Unsubscribe
                        </Button>
                      )}
                    </Box>
                  </Box>
                ))}
            </SimpleGrid>
          </TabPanel>

          {(Auth.user.isSuperAdmin || !isPartner) && (<TabPanel>
            <SimpleGrid columns={{ base: 1, md: 1, lg: 1 }} spacing="20px">
              {availableAddons.map((addon, index) => (
                <Box
                  key={index}
                  borderWidth="1px"
                  borderRadius="lg"
                  mb={4}
                  width="100%"
                >
                  <Box
                    bg="green.500"
                    color="white"
                    p={4}
                    borderTopRadius="lg"
                    position="relative"
                  >
                    <Text>{addon.name}</Text>
                    <Text>{addon.description}</Text>
                    {activeAddons.includes(addon._id) && (
                      <Text color="green.500">Active</Text>
                    )}
                  </Box>
                  <Box p={4}>
                    <VStack align="left">
                      {addon.items.map(([product, plan], itemIndex) => (
                        <Flex justify="space-between" key={itemIndex} mb={4}>
                          <Box flex="1" textAlign="left" ml={4}>
                            <Text fontWeight="bold" paddingRight="10px">
                              {product.name}:
                            </Text>
                            {isLicensedPrice(plan) ? (
                              !isLoadedPartner && renderLicensedPrice(product, plan, { addon: true })
                            ) : plan.billing_scheme === "tiered" ? (
                              <>
                                {formatTieredPricing(
                                  plan.tiers,
                                  plan.tiers_mode,
                                  product.unit_label,
                                  usageTypeOf(plan),
                                  plan.unit_amount_decimal || plan.amount || 0
                                )}
                              </>
                            ) : (
                              <Text>
                                Price: $
                                {(
                                  (plan.unit_amount_decimal ||
                                    plan.amount ||
                                    0) / 100
                                ).toFixed(3)}
                                /
                                {product.unit_label
                                  ? product.unit_label
                                  : "unit"}
                              </Text>
                            )}
                          </Box>
                        </Flex>
                      ))}
                    </VStack>
                    {!activeAddons.includes(addon._id) ? (
                      <Button
                        size="sm"
                        mt={4}
                        onClick={() => subscribeAddon(addon._id)}
                      >
                        Subscribe
                      </Button>
                    ) : (
                      <Button
                        size="sm"
                        mt={4}
                        onClick={() => removeAddon(addon._id)}
                      >
                        Remove
                      </Button>
                    )}
                  </Box>
                </Box>
              ))}
            </SimpleGrid>
          </TabPanel>)}
        </TabPanels>
      </Tabs>

      {/* While a discount is being created the modal cannot be finished or dismissed:
          continuing would send the promotion array without the code that is still on
          its way, and closing would let the late response push a coupon entry into a
          modal the admin had already cancelled. isCreatingCoupon is only ever true for
          a superadmin using the block below, so nothing changes for anyone else. */}
      <Modal
        isOpen={isPromotionModalOpen}
        onClose={handleModalCloseOrCancel}
        closeOnOverlayClick={!isCreatingCoupon}
        closeOnEsc={!isCreatingCoupon}
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Confirm</ModalHeader>
          <ModalCloseButton onClick={handleModalCloseOrCancel} isDisabled={isCreatingCoupon} />
          <ModalBody>
            <Text mb={2} fontWeight="semibold">
              Promotion Code (optional)
            </Text>

            {/* Multiple promo inputs with Add & Remove */}
            {promotionCodes.map((code, idx) => (
              <Flex key={idx} align="center" mb={idx === promotionCodes.length - 1 ? 0 : 2}>
                <Input
                  placeholder={`Promotion Code ${idx + 1}`}
                  value={code}
                  onChange={(e) => {
                    const next = [...promotionCodes];
                    next[idx] = e.target.value;
                    setPromotionCodes(next);
                  }}
                />
                <Button
                  ml={2}
                  size="sm"
                  variant="ghost"
                  onClick={() => removePromotionAt(idx)}
                  isDisabled={promotionCodes.length === 1 && !promotionCodes[0]}
                >
                  Remove
                </Button>
              </Flex>
            ))}
            <Button
              mt={2}
              size="sm"
              variant="outline"
              onClick={() => setPromotionCodes([...promotionCodes, ""])}
            >
              + Add another
            </Button>

            {/* SUPERADMIN ONLY — the modal is also open to office admins, who must keep
                seeing exactly the free-text boxes above and nothing else. */}
            {Auth.user && Auth.user.isSuperAdmin && internalCouponsSupported && (
              <>
                <Text mb={2} mt={4} fontWeight="semibold">
                  Existing discounts
                </Text>
                {internalCoupons.length === 0 && (
                  <Text fontSize="sm" mb={2}>
                    No fixed-dollar discounts exist yet.
                  </Text>
                )}
                {internalCoupons.map((coupon) => (
                  <Flex key={coupon.id} align="center" mb={2}>
                    <Box flex="1">
                      <Text fontSize="sm">
                        {formatCouponAmount(coupon)} off {describeCouponDuration(coupon)}
                      </Text>
                      <Text fontSize="xs" color="gray.500">
                        {coupon.name}
                      </Text>
                      {coupon.restricted && (
                        <Text fontSize="xs" color="orange.500">
                          Restricted: {describeCouponRestriction(coupon)}
                        </Text>
                      )}
                    </Box>
                    <Button
                      ml={2}
                      size="sm"
                      variant="outline"
                      onClick={() => addPromotionEntry(coupon.code)}
                    >
                      Use
                    </Button>
                  </Flex>
                ))}

                {!showCouponForm && (
                  <Button
                    mt={2}
                    size="sm"
                    variant="outline"
                    onClick={() => setShowCouponForm(true)}
                  >
                    Create a discount
                  </Button>
                )}

                {showCouponForm && (
                  <Box mt={3} p={3} borderWidth="1px" borderRadius="md">
                    <Text mb={2} fontWeight="semibold">
                      Create a discount
                    </Text>
                    <Input
                      type="number"
                      min="1"
                      step="1"
                      placeholder="Amount in whole dollars"
                      value={couponAmount}
                      onChange={(e) => {
                        const val = e.target.value;
                        if (val === "" || /^\d+$/.test(val)) {
                          setCouponAmount(val);
                        }
                      }}
                    />
                    <RadioGroup mt={3} onChange={setCouponDuration} value={couponDuration}>
                      <Stack direction="row">
                        <Radio value="once">One time</Radio>
                        <Radio value="repeating">Number of months</Radio>
                        <Radio value="forever">Forever</Radio>
                      </Stack>
                    </RadioGroup>
                    {couponDuration === "repeating" && (
                      <Input
                        mt={3}
                        type="number"
                        min="1"
                        step="1"
                        placeholder="Number of months"
                        value={couponMonths}
                        onChange={(e) => {
                          const val = e.target.value;
                          if (val === "" || /^\d+$/.test(val)) {
                            setCouponMonths(val);
                          }
                        }}
                      />
                    )}
                    <Flex mt={3}>
                      <Button
                        size="sm"
                        colorScheme="blue"
                        isLoading={isCreatingCoupon}
                        onClick={handleCreateCoupon}
                      >
                        Create &amp; add
                      </Button>
                      <Button size="sm" ml={2} variant="ghost" onClick={resetCouponForm}>
                        Cancel
                      </Button>
                    </Flex>
                  </Box>
                )}
              </>
            )}

            {Auth.user && Auth.user.isSuperAdmin && isPartner && (
              <>
                <Text mb={2} mt={4} fontWeight="semibold">
                  Subscription Start Date (optional)
                </Text>
                <Input
                  type="date"
                  value={subscriptionStartDate}
                  min={moment().add(1, "days").format("YYYY-MM-DD")}
                  onChange={(e) => setSubscriptionStartDate(e.target.value)}
                  placeholder="Leave empty if you want to start now"
                />
              </>
            )}
            {Auth.user && Auth.user.isSuperAdmin && !isPartner && (
              <>
                <Text mb={2} mt={4} fontWeight="semibold">
                  Number of Trial Days (optional)
                </Text>
                <Input
                  type="number"
                  placeholder="Leave empty or enter a number"
                  value={trialDays}
                  onChange={(e) => {
                    const val = e.target.value;
                    if (val === "" || /^\d+$/.test(val)) {
                      setTrialDays(val);
                    }
                  }}
                />

                <Checkbox
                  mt={3}
                  isChecked={sendInvoice}
                  onChange={(e) => setSendInvoice(e.target.checked)}
                >
                  Send Invoice (won&apos;t charge automatically)
                </Checkbox>
              </>
            )}
            {Auth.user?.isSuperAdmin && hasSiblingLocations && isSubscriptionUpdate && (
              <Checkbox
                mt={3}
                isChecked={autoScaleMetered}
                onChange={(e) => setAutoScaleMetered(e.target.checked)}
              >
                Auto-scale metered pricing for multi-location
              </Checkbox>
            )}
          </ModalBody>
          <ModalFooter>
            <Button
              variant="ghost"
              mr={3}
              onClick={handleModalCloseOrCancel}
              isDisabled={isCreatingCoupon}
            >
              Cancel
            </Button>
            <Button colorScheme="blue" onClick={handleModalContinue} isDisabled={isCreatingCoupon}>
              Continue
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
}

export default Subscription;
